import { useState } from 'react';
import type { FormEvent } from 'react';
// The full metadata, as the leads endpoint uses: the default one passes numbers it then refuses (#27969).
import { parsePhoneNumberFromString } from 'libphonenumber-js/max';
import PhoneField from './PhoneField';

type PlanId = 'basic' | 'basic-plus' | 'unsure';

interface Plan {
  id: PlanId;
  name: string;
  note: string;
}

const PLANS: Plan[] = [
  { id: 'basic', name: 'Basic', note: 'White-label web app' },
  { id: 'basic-plus', name: 'Basic Plus', note: 'Web + iOS and Android' },
  { id: 'unsure', name: 'Not sure yet', note: 'Help me choose' },
];

export interface DemoRequest {
  name: string;
  club: string;
  email: string;
  phone: string;
  courts: string;
  sport: string;
  plan: PlanId;
  message: string;
}

const EMPTY: DemoRequest = {
  name: '',
  club: '',
  email: '',
  phone: '',
  courts: '',
  sport: '',
  plan: 'basic-plus',
  message: '',
};

type DemoErrors = Partial<Record<'name' | 'club' | 'email' | 'phone' | 'courts', string>>;

type DemoField = keyof DemoErrors;

/**
 * #27969 — the API answers a refused request with one sentence and no field name. Put it under the
 * field it is about, so it reads like the form's own checks; anything else is shown above the button.
 */
export function serverFieldFor(reason: string): DemoField | undefined {
  const text = reason.toLowerCase();
  if (text.includes('phone')) return 'phone';
  if (text.includes('email')) return 'email';
  if (text.includes('court')) return 'courts';
  if (text.includes('club')) return 'club';
  if (text.includes('name')) return 'name';
  return undefined;
}

const EMAIL = /^[^\s@]+@[^\s@.]+(\.[^\s@.]+)*\.[a-z]{2,}$/i;

/**
 * #27901 — the same rules the leads endpoint enforces, checked here so each field says what is
 * wrong before anything is sent, instead of one alert after the request is refused.
 */
export function demoErrors(form: DemoRequest): DemoErrors {
  const errors: DemoErrors = {};
  const name = form.name.trim();
  const club = form.club.trim();
  const email = form.email.trim();
  if (!name) errors.name = 'Enter your name.';
  else if (name.length < 2) errors.name = 'Your name needs at least 2 characters.';
  if (!club) errors.club = 'Enter your club or business name.';
  else if (club.length < 2) errors.club = 'The club name needs at least 2 characters.';
  if (!email) errors.email = 'Enter your email.';
  else if (!EMAIL.test(email)) errors.email = 'Enter a valid email, like you@yourclub.com.';
  // Optional, but a number that is there must be one we can call; the field itself says why.
  if (form.phone && !parsePhoneNumberFromString(form.phone)?.isValid()) errors.phone = 'invalid';
  if (form.courts) {
    const courts = Number(form.courts);
    if (!Number.isInteger(courts) || courts < 1 || courts > 999) errors.courts = 'Enter a whole number from 1 to 999.';
  }
  return errors;
}

const SPORTS = [
  'Pickleball', 'Tennis', 'Badminton', 'Basketball',
  'Football', 'Golf', 'Squash', 'Table tennis', 'Multi-sport facility',
];

export default function Demo() {
  const [form, setForm] = useState<DemoRequest>(EMPTY);
  const [sent, setSent] = useState(false);
  // Errors show once they try to send, or for a field they have filled in and left; then they
  // update as each field is corrected.
  const [tried, setTried] = useState(false);
  const [left, setLeft] = useState<Partial<Record<keyof DemoErrors, true>>>({});
  // What the server refused, by field, until that field is changed; and a reason with no field.
  const [serverErrors, setServerErrors] = useState<DemoErrors>({});
  const [failure, setFailure] = useState('');
  const allErrors: DemoErrors = { ...serverErrors, ...demoErrors(form) };
  const shown = (key: keyof DemoErrors) => (tried || left[key] || serverErrors[key] ? allErrors[key] : undefined);
  const fieldClass = (key: keyof DemoErrors) => (shown(key) ? 'field bad' : 'field');
  const invalid = (key: keyof DemoErrors) => (shown(key) ? { 'aria-invalid': true, 'aria-describedby': `${key}-err` } : {});
  const message = (key: keyof DemoErrors) => (shown(key) ? <p className="field-err" id={`${key}-err`}>{shown(key)}</p> : null);
  // Leaving an empty field says nothing; leaving one with something in it checks it.
  const leave = (key: keyof DemoErrors) => () => {
    if (form[key].trim()) setLeft((prev) => ({ ...prev, [key]: true }));
  };

  const set = <K extends keyof DemoRequest>(key: K, value: DemoRequest[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setServerErrors((prev) => {
      if (!(key in prev)) return prev;
      const next = { ...prev };
      delete next[key as DemoField];
      return next;
    });
    setFailure('');
  };

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setTried(true);
    setFailure('');
    const problems = demoErrors(form);
    if (Object.keys(problems).length) {
      // Take them to the first field that needs fixing.
      const first = (['name', 'club', 'email', 'phone', 'courts'] as const).find((key) => problems[key]);
      document.getElementById(first ?? 'name')?.focus();
      return;
    }

    try {
      // Submit the demo request to the backend
      // VITE_API_BASE_URL is what .env sets (as in the other two frontends); VITE_API_URL was never set,
      // so every deployed build posted to localhost.
      const apiUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8080/api';
      const response = await fetch(`${apiUrl}/leads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, phone: form.phone || undefined, courts: form.courts ? Number(form.courts) : undefined }),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => null);
        console.error('Demo request failed:', error);
        // Show the API's reason (e.g. an invalid email) under its field when it sends one.
        const reasons: string[] = (Array.isArray(error?.message) ? error.message : [error?.message])
          .filter((reason: unknown): reason is string => typeof reason === 'string' && reason.length > 0);
        const byField: DemoErrors = {};
        const general: string[] = [];
        for (const reason of reasons) {
          const field = serverFieldFor(reason);
          if (field && !byField[field]) byField[field] = reason;
          else general.push(reason);
        }
        setServerErrors(byField);
        setFailure(general.join(' ') || (reasons.length ? '' : 'We could not send your request. Please try again.'));
        const first = (['name', 'club', 'email', 'phone', 'courts'] as const).find((key) => byField[key]);
        if (first) document.getElementById(first)?.focus();
        return;
      }

      const data = await response.json();
      console.info('Demo request submitted:', data);

      setSent(true);
      setTried(false);
      setLeft({});
      setForm(EMPTY);
    } catch (error) {
      console.error('Demo request error:', error);
      setFailure('We could not send your request. Please check your connection and try again.');
    }
  };

  return (
    <section id="demo" className="demo-section">
      <div className="form-layout">
        <section className="form-main">
          <p className="eyebrow">Request a demo</p>
          <h2
            className="h-lg"
            style={{ marginTop: '18px', fontSize: 'clamp(34px, 4.2vw, 56px)', lineHeight: 1 }}
          >
            Tell us about<br />your club.
          </h2>
          <p
            style={{
              fontSize: '17px',
              lineHeight: 1.6,
              color: 'var(--ink-2)',
              marginTop: '18px',
              maxWidth: '520px',
            }}
          >
            A 30-minute walkthrough of PlaySlot on your courts, your sports and your membership
            structure. No card, no commitment.
          </p>

          <form onSubmit={onSubmit} noValidate>
            <div className="field-grid">
              <div>
                <label className="lbl" htmlFor="name">Full name</label>
                <input
                  className={fieldClass('name')} id="name" name="name" type="text" placeholder="Your name"
                  autoComplete="name" required maxLength={120} {...invalid('name')}
                  value={form.name} onChange={(e) => set('name', e.target.value)} onBlur={leave('name')}
                />
                {message('name')}
              </div>
              <div>
                <label className="lbl" htmlFor="club">Club / business name</label>
                <input
                  className={fieldClass('club')} id="club" name="club" type="text" placeholder="Your club"
                  autoComplete="organization" required maxLength={160} {...invalid('club')}
                  value={form.club} onChange={(e) => set('club', e.target.value)} onBlur={leave('club')}
                />
                {message('club')}
              </div>
              <div>
                <label className="lbl" htmlFor="email">Email</label>
                <input
                  className={fieldClass('email')} id="email" name="email" type="email" placeholder="you@yourclub.com"
                  autoComplete="email" required {...invalid('email')}
                  value={form.email} onChange={(e) => set('email', e.target.value)} onBlur={leave('email')}
                />
                {message('email')}
              </div>
              <div>
                <PhoneField id="phone" value={form.phone} onChange={(v) => set('phone', v)} showError={tried} error={serverErrors.phone} />
              </div>
              <div>
                <label className="lbl" htmlFor="courts">Number of courts</label>
                <input
                  className={fieldClass('courts')} id="courts" name="courts" type="number" min={1} max={999} step={1} placeholder="e.g. 6"
                  {...invalid('courts')}
                  value={form.courts} onChange={(e) => set('courts', e.target.value)} onBlur={leave('courts')}
                />
                {message('courts')}
              </div>
              <div>
                <label className="lbl" htmlFor="sport">Primary sport</label>
                <select
                  className="field" id="sport" name="sport"
                  value={form.sport} onChange={(e) => set('sport', e.target.value)}
                >
                  <option value="">Select a sport</option>
                  {SPORTS.map((s) => (
                    <option key={s}>{s}</option>
                  ))}
                </select>
              </div>
            </div>

            <div style={{ marginTop: '24px' }}>
              <span className="lbl" id="plan-label">Interested plan</span>
              <div className="plan-picker" role="group" aria-labelledby="plan-label">
                {PLANS.map((plan) => (
                  <button
                    key={plan.id}
                    className="plan-opt"
                    type="button"
                    aria-pressed={form.plan === plan.id}
                    onClick={() => set('plan', plan.id)}
                  >
                    <span style={{ display: 'flex', alignItems: 'center', gap: '9px' }}>
                      <span className="radio"><i /></span>
                      {plan.name}
                    </span>
                    <span className="note">{plan.note}</span>
                  </button>
                ))}
              </div>
            </div>

            <div style={{ marginTop: '24px' }}>
              <label className="lbl" htmlFor="message">Message</label>
              <textarea
                className="field" id="message" name="message"
                placeholder="What are you running today, and what is getting in the way?"
                value={form.message} onChange={(e) => set('message', e.target.value)}
              />
            </div>

            <div
              style={{
                display: 'flex', flexWrap: 'wrap', alignItems: 'center',
                gap: '22px', marginTop: '28px',
              }}
            >
              <button className="btn btn-primary" type="submit" style={{ minWidth: '210px' }}>
                Request a Demo
              </button>
              <span
                style={{ fontSize: '13px', color: 'var(--ink-4)', maxWidth: '280px', lineHeight: 1.5 }}
              >
                We reply within one business day. Your details stay with PlaySlot.
              </span>
            </div>

            {failure && <p className="form-error" role="alert">{failure}</p>}

            <p className={`form-success${sent ? ' show' : ''}`} role="status" tabIndex={-1}>
              Thanks &mdash; your request has been noted. We&rsquo;ll be in touch within one business day.
            </p>
          </form>
        </section>

    <aside className="form-side">
      <div style={{ position: 'absolute', right: '-120px', top: '120px', width: '420px', height: '420px', border: '1px solid rgba(200,255,60,.07)', borderRadius: '4px', transform: 'rotate(28deg)' }} aria-hidden="true"></div>
      <div style={{ position: 'absolute', right: '-60px', top: '300px', width: '280px', height: '280px', background: 'radial-gradient(50% 50% at 50% 50%, rgba(200,255,60,.12), transparent 70%)', filter: 'blur(16px)' }} aria-hidden="true"></div>

      <div style={{ position: 'relative' }}>
        <h2 className="h-md">Let&rsquo;s build your perfect sports experience.</h2>
        <p style={{ fontSize: '15px', lineHeight: '1.6', color: 'var(--ink-3)', marginTop: '16px' }}>
          Every club runs differently. We will set the demo up around the way yours actually works.
        </p>

        <div style={{ marginTop: '34px' }}>
          <div className="side-row">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true"><rect x="3" y="5" width="18" height="16" rx="2"></rect><path d="M3 10h18M8 3v4M16 3v4"></path></svg>
            <span style={{ fontSize: '15px', fontWeight: '600' }}>Reservations</span>
          </div>
          <div className="side-row">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true"><rect x="2.5" y="5" width="19" height="14" rx="2.5"></rect><path d="M2.5 10h19M6 15h4"></path></svg>
            <span style={{ fontSize: '15px', fontWeight: '600' }}>Memberships</span>
          </div>
          <div className="side-row">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true"><path d="M4 5h5v5H4zM4 14h5v5H4zM15 9.5h5v5h-5z"></path><path d="M9 7.5h3v9h3"></path></svg>
            <span style={{ fontSize: '15px', fontWeight: '600' }}>Events</span>
          </div>
          <div className="side-row">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true"><path d="M12 3v18"></path><path d="M16.5 7.5c0-1.9-2-3-4.5-3s-4.5 1-4.5 3 2 2.6 4.5 3.2 4.5 1.3 4.5 3.3-2 3-4.5 3-4.5-1.1-4.5-3"></path></svg>
            <span style={{ fontSize: '15px', fontWeight: '600' }}>Payments</span>
          </div>
          <div className="side-row">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true"><path d="M4 19V11M10 19V5M16 19v-6M22 19H2"></path></svg>
            <span style={{ fontSize: '15px', fontWeight: '600' }}>Analytics</span>
          </div>
        </div>

        <div style={{ marginTop: '40px', border: '1px solid rgba(255,255,255,.10)', borderRadius: '14px', background: 'rgba(10,13,14,.8)', padding: '22px' }}>
          <div className="mono" style={{ fontSize: '10px', letterSpacing: '.16em', color: 'var(--ink-4)' }}>WHAT HAPPENS NEXT</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', marginTop: '16px' }}>
            <div className="step"><span className="n">01</span><span style={{ fontSize: '14px', color: '#C6CDC7' }}>We read your setup and send times that suit you.</span></div>
            <div className="step"><span className="n">02</span><span style={{ fontSize: '14px', color: '#C6CDC7' }}>A 30-minute walkthrough on your sport and courts.</span></div>
            <div className="step"><span className="n">03</span><span style={{ fontSize: '14px', color: '#C6CDC7' }}>A plan and a go-live date built for your season.</span></div>
          </div>
        </div>
      </div>
    </aside>
      </div>
    </section>
  );
}
